import React, { useEffect, useId, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import GoogleIcon from "@mui/icons-material/Google";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExternalLinkIcon from "@mui/icons-material/OpenInNew";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import LoadingIcon from "@mui/icons-material/Autorenew";
import PreviewIcon from "@mui/icons-material/CropFree";
import MinimizeIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import UploadIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { GoogleGalleryImage, searchGoogleGalleryImages } from "../../utils/googleImages";
import useGoogleImageUpload from "./useGoogleImageUpload";
import GoogleImageUploadStatus, { GalleryConnectionBadge } from "./GoogleImageUploadStatus";
import styles from "./GoogleImageGallery.module.scss";
import { SearchInput } from "../common/ClearControls";

type Props = {
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
  initialQuery?: string;
  multiple?: boolean;
  // Editors with custom overlays may sit above the standard MUI modal layer.
  zIndex?: number;
};

function sourceName(image: GoogleGalleryImage) {
  try { return new URL(image.link).hostname.replace(/^www\./, ""); }
  catch { return image.displayLink || "Image source"; }
}

function ImagePreview({ image, large = false }: { image: GoogleGalleryImage; large?: boolean }) {
  const [failed, setFailed] = useState(false);
  const url = large ? image.link : image.image?.thumbnailLink || image.link;
  useEffect(() => setFailed(false), [url]);
  return failed ? <span className={styles.imageFallback}><ImageIcon /><span>Preview unavailable</span></span>
    : <img src={url} alt={large ? `Preview of ${image.title || "selected image"}` : image.title || "Image result"}
      onError={() => setFailed(true)} loading="lazy" />;
}

export default function GoogleImageGalleryDialog({ open, onClose, onUploadSuccess, initialQuery = "", multiple = false, zIndex }: Props) {
  const titleId = useId();
  const previewTitleId = useId();
  const [query, setQuery] = useState(initialQuery);
  const [searchedQuery, setSearchedQuery] = useState("");
  const [results, setResults] = useState<GoogleGalleryImage[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [selected, setSelected] = useState<GoogleGalleryImage[]>([]);
  const [confidential, setConfidential] = useState(false);
  const [preview, setPreview] = useState<GoogleGalleryImage | null>(null);
  const [importing, setImporting] = useState(false);
  const [batchPosition, setBatchPosition] = useState({ current: 0, total: 0 });
  const searchRequest = useRef<AbortController | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const importRunning = useRef(false);
  const initialized = useRef(false);
  const uploader = useGoogleImageUpload();

  const search = async (searchPage = 1, searchTerm = query) => {
    if (!searchTerm.trim() || importRunning.current || !uploader.connection.online) return;
    searchRequest.current?.abort();
    const controller = new AbortController();
    searchRequest.current = controller;
    let timedOut = false;
    const timer = window.setTimeout(() => { timedOut = true; controller.abort(); }, 25_000);
    setSearching(true);
    setSearchError("");
    try {
      const data = await searchGoogleGalleryImages(searchTerm, searchPage, controller.signal);
      if (controller.signal.aborted) return;
      setResults(data.items);
      setHasNextPage(data.hasNextPage);
      setPage(searchPage);
      setSearchedQuery(searchTerm.trim());
    } catch (error) {
      if (!controller.signal.aborted || timedOut) {
        setSearchError(timedOut ? "Search is taking longer than expected. Check your connection and try again."
          : error instanceof Error ? error.message : "Search failed. Please try again.");
      }
    } finally {
      window.clearTimeout(timer);
      if (searchRequest.current === controller) setSearching(false);
    }
  };

  useEffect(() => {
    if (!open || initialized.current) return;
    initialized.current = true;
    if (initialQuery.trim()) void search(1, initialQuery);
    // Start with the gallery's existing query once; reopening preserves the current selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => () => searchRequest.current?.abort(), []);
  useEffect(() => {
    if (importing && open && bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [importing, open]);

  const select = (image: GoogleGalleryImage) => {
    if (importRunning.current) return;
    uploader.reset();
    setSelected((current) => current.some((item) => item.link === image.link)
      ? current.filter((item) => item.link !== image.link) : multiple ? [...current, image] : [image]);
  };

  const uploadSelected = async () => {
    if (importRunning.current || !selected.length || !uploader.connection.online) return;
    importRunning.current = true;
    setImporting(true);
    const images = [...selected];
    let completed = 0;
    try {
      for (const [index, image] of images.entries()) {
        setBatchPosition({ current: index + 1, total: images.length });
        const data = new FormData();
        data.append("image_url", image.link);
        data.append("is_confidential", String(confidential));
        const response = await uploader.upload(data);
        if (!response) return;
        completed += 1;
        setSelected((current) => current.filter((item) => item.link !== image.link));
      }
      setConfidential(false);
      onUploadSuccess?.();
      toast.success(images.length === 1 ? "Image added to your private gallery." : `${images.length} images added to your private gallery.`);
      onClose();
    } catch (error) {
      if ((error as { code?: string })?.code === "ERR_CANCELED") return;
      if (completed) onUploadSuccess?.();
      toast.error(error instanceof Error ? error.message : "Could not save this image. Please try again.");
    } finally {
      importRunning.current = false;
      setImporting(false);
    }
  };

  const selectedImage = selected[0];
  const clearSearch = () => {
    searchRequest.current?.abort();
    setQuery("");
    setSearchedQuery("");
    setResults([]);
    setPage(1);
    setHasNextPage(false);
    setSearching(false);
    setSearchError("");
  };
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth aria-labelledby={titleId} PaperProps={{ className: styles.dialog }}
        sx={zIndex === undefined ? undefined : { zIndex }}>
        <header className={styles.header}>
          <div className={styles.heading}>
            <span className={styles.googleMark}><GoogleIcon /></span>
            <div><h2 id={titleId}>Add images from Google</h2>
             </div>
          </div>
          <div className={styles.headerActions}>
            <GalleryConnectionBadge connection={uploader.connection} />
            <button type="button" className={styles.iconButton} onClick={onClose}
              aria-label={importing ? "Minimize image upload" : "Close Google image search"}
              title={importing ? "Upload continues in the background" : "Close"}>
              {importing ? <MinimizeIcon /> : <CloseIcon />}
            </button>
          </div>
        </header>

        <div className={styles.body} ref={bodyRef}>
          {!uploader.connection.online && <p className={styles.notice} role="status">You're offline. Reconnect to search or upload; your selection is saved here.</p>}
          <form className={styles.searchBar} onSubmit={(event) => { event.preventDefault(); void search(); }}>
            <label className={styles.searchField}><SearchIcon aria-hidden="true" /><span className={styles.srOnly}>Search Google images</span>
              <SearchInput type="search" placeholder="Search Google Images..." value={query} onChange={(event) => setQuery(event.target.value)} onClear={clearSearch} clearLabel="Clear Google image search" disabled={importing} autoFocus />
            </label>
            <button type="submit" className={styles.primaryButton} disabled={importing || searching || !query.trim() || !uploader.connection.online}>
              {searching ? <LoadingIcon className={styles.spinner} /> : <SearchIcon />} {searching ? "Searching…" : "Search"}
            </button>
          </form>

          {importing && <div className={styles.progressArea}>
            {batchPosition.total > 1 && <p className={styles.batchLabel}>Image {batchPosition.current} of {batchPosition.total}</p>}
            <GoogleImageUploadStatus state={uploader.state} connection={uploader.connection} elapsedSeconds={uploader.elapsedSeconds} />
            <p className={styles.backgroundHint}>You can hide this window. The upload will continue while you stay on this page.</p>
          </div>}
          {searchError && <div className={styles.error} role="alert"><span>{searchError}</span><button type="button" onClick={() => void search()}>Try search again</button></div>}
          {uploader.state.phase === "error" && <div className={styles.error} role="alert">{uploader.state.error} Your selection is kept so you can retry.</div>}

          <div className={styles.browserLayout}>
            <section className={styles.results} aria-label="Google image results" aria-busy={searching}>
              <div className={styles.resultsHeading}><strong>{searchedQuery ? `Results for “${searchedQuery}”` : "Find the right image"}</strong>
                {searchedQuery && <span>{results.length} images · Page {page}</span>}</div>
              {searching ? <div className={styles.skeletonGrid} role="status" aria-label="Searching Google Images">{Array.from({ length: 6 }, (_, index) => <span key={index} />)}</div>
                : !results.length ? <div className={styles.empty}><ImageIcon /><h3>{searchedQuery ? "No results found." : "What would you like to show?"}</h3>
                  <p>{searchedQuery ? "Try a different term, such as a treatment or dental condition." : "Search a treatment, condition, or topic to get started."}</p>
                  {!searchedQuery && <div className={styles.suggestions}>{["Dental implants", "Tooth anatomy", "Brushing teeth"].map((term) =>
                    <button type="button" key={term} onClick={() => { setQuery(term); void search(1, term); }} disabled={!uploader.connection.online}>{term}</button>)}</div>}</div>
                  : <div className={styles.imageGrid}>{results.map((image, index) => {
                    const isSelected = selected.some((item) => item.link === image.link);
                    return <article className={`${styles.imageCard} ${isSelected ? styles.selectedCard : ""}`} key={image.link}>
                      <button type="button" className={styles.selectImage} onClick={() => select(image)} disabled={importing}
                        aria-label={`Select ${image.title || `image ${index + 1}`}`} aria-pressed={isSelected}>
                        <span className={styles.thumbnail}><ImagePreview image={image} />{isSelected && <span className={styles.selectedMark}><CheckIcon /></span>}</span>
                        <span className={styles.imageTitle}>{image.title || "Untitled image"}</span><span className={styles.source}>{sourceName(image)}</span>
                      </button>
                      <button type="button" className={styles.previewButton} onClick={() => setPreview(image)} aria-label={`Preview ${image.title || `image ${index + 1}`}`}><PreviewIcon /></button>
                    </article>;
                  })}</div>}
              {searchedQuery && <nav className={styles.pagination} aria-label="Image search pages">
                <button type="button" disabled={page === 1 || searching || importing} onClick={() => void search(page - 1, searchedQuery)}><ChevronLeftIcon /> Previous</button>
                <span>Page {page}</span><button type="button" disabled={!hasNextPage || searching || importing} onClick={() => void search(page + 1, searchedQuery)}>Next <ChevronRightIcon /></button>
              </nav>}
            </section>
            <aside className={styles.selection} aria-label="Selected image">
              <p className={styles.eyebrow}>{multiple ? "YOUR SELECTION" : "SELECTED IMAGE"}</p>
              {selectedImage ? <><div className={styles.selectedPreview}><ImagePreview image={selectedImage} /></div>
                <strong>{selectedImage.title || "Selected image"}</strong><span className={styles.source}>{sourceName(selectedImage)}</span>
                {selectedImage.image?.width && selectedImage.image?.height && <span className={styles.dimensions}>{selectedImage.image.width} × {selectedImage.image.height} px</span>}
                {multiple && selected.length > 1 && <p>{selected.length} images selected</p>}
                <button type="button" className={styles.secondaryButton} onClick={() => setPreview(selectedImage)}><PreviewIcon /> View larger</button>
              </> : <div className={styles.selectionEmpty}><ImageIcon /><p>Select {multiple ? "one or more images" : "an image"} to preview your selection here.</p></div>}
              <label className={styles.confidential}><input type="checkbox" checked={confidential} onChange={(event) => setConfidential(event.target.checked)} disabled={importing} />
                <span><strong>Mark as confidential</strong><small>Keep this image marked as private content.</small></span></label>
            </aside>
          </div>
        </div>

        <footer className={styles.footer}>
          <p>{importing ? "We'll let you know when your image is ready." : selected.length ? `${selected.length} image${selected.length === 1 ? "" : "s"} selected` : "Select an image to add to your gallery."}</p>
          <div className={styles.footerActions}><button type="button" className={styles.secondaryButton} onClick={onClose}>{importing ? "Hide while uploading" : "Cancel"}</button>
            <button type="button" className={styles.primaryButton} onClick={() => void uploadSelected()} disabled={importing || searching || !selected.length || !uploader.connection.online}>
              {importing ? <LoadingIcon className={styles.spinner} /> : <UploadIcon />}
              {importing ? "Uploading..." : uploader.state.phase === "error" ? "Try upload again" : "Add to private gallery"}
            </button></div>
        </footer>
      </Dialog>
      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="md" fullWidth aria-labelledby={previewTitleId} PaperProps={{ className: styles.previewDialog }}
        sx={zIndex === undefined ? undefined : { zIndex: zIndex + 1 }}>
        {preview && <><div className={styles.previewHeader}><strong id={previewTitleId}>Image preview</strong><button type="button" className={styles.iconButton} onClick={() => setPreview(null)} aria-label="Close image preview"><CloseIcon /></button></div>
          <div className={styles.largePreview}><ImagePreview image={preview} large /></div>
          <a className={styles.sourceLink} href={preview.link} target="_blank" rel="noopener noreferrer">Open source image <ExternalLinkIcon /></a></>}
      </Dialog>
    </>
  );
}
